import {
  RICH_CONTENT_TAGS,
  RichContentTagType,
  ExtractedContent,
} from "@/config/richContentConfig";
import { Tables } from "@/types/database.types";

/**
 * Parse rich content tags from a message
 * @param content The message content
 * @returns An object with the cleaned content and extracted rich elements
 */
export const parseRichContent = (content: string) => {
  const results: {
    cleanContent: string;
    insideContent: ExtractedContent[];
    belowContent: ExtractedContent[];
    bottomContent: ExtractedContent[];
  } = {
    cleanContent: content,
    insideContent: [],
    belowContent: [],
    bottomContent: [],
  };

  // Process each content type
  Object.entries(RICH_CONTENT_TAGS).forEach(([type, config]) => {
    const tagType = type as RichContentTagType;

    // Process all matches
    let match;
    let processedContent = results.cleanContent;

    // Reset regex lastIndex
    config.regex.lastIndex = 0;

    while ((match = config.regex.exec(results.cleanContent)) !== null) {
      const rawMatch = match[0];

      // Extract content and attributes
      const contentPart = match[2] || match[1] || ""; // content is in group 2 or 1 depending on regex
      const attributesPart = match[1] || "";

      const attributes: Record<string, string> = {};

      // Special handling for scheduling widget direct extraction from regex match
      if (tagType === "SCHEDULING") {
        // If attributes are present, parse them
        if (config.attributeRegex && attributesPart) {
          let attrMatch;
          config.attributeRegex.lastIndex = 0;
          while (
            (attrMatch = config.attributeRegex.exec(attributesPart)) !== null
          ) {
            const [_, key, value] = attrMatch;
            attributes[key] = value;
          }
        }
        // If no url attribute, treat contentPart as the url (direct format)
        if (!attributes.url && contentPart) {
          attributes.url = contentPart.trim();
        }
      } else {
        // Parse attributes if defined for this tag (for non-scheduling tags)
        if (config.attributeRegex && attributesPart) {
          let attrMatch;
          config.attributeRegex.lastIndex = 0;
          while (
            (attrMatch = config.attributeRegex.exec(attributesPart)) !== null
          ) {
            const [_, key, value] = attrMatch;
            attributes[key] = value;
          }
        }
      }

      // Validate required attributes
      const isValid = validateContent(tagType, attributes);

      if (isValid) {
        // Create extracted content object
        const extracted: ExtractedContent = {
          type: tagType,
          content: contentPart.trim(),
          attributes:
            Object.keys(attributes).length > 0 ? attributes : undefined,
          rawMatch,
        };

        // Add to the appropriate location collection
        switch (config.location) {
          case "inside":
            results.insideContent.push(extracted);
            break;
          case "below":
            results.belowContent.push(extracted);
            break;
          case "bottom":
            results.bottomContent.push(extracted);
            break;
        }

        // Remove from content if it should be displayed elsewhere
        if (config.location !== "inside") {
          processedContent = processedContent.replace(rawMatch, "");
        }
      }
    }

    // Update the clean content after processing this tag type
    results.cleanContent = processedContent;
  });

  // Trim the clean content and return results
  results.cleanContent = results.cleanContent.trim();
  return results;
};

/**
 * Filter rich content results based on agent capabilities
 * @param richContent The parsed rich content
 * @param agent The agent object with capability information
 * @returns Filtered rich content with disabled capabilities removed
 */
export const filterRichContentByCapabilities = (
  richContent: {
    cleanContent: string;
    insideContent: ExtractedContent[];
    belowContent: ExtractedContent[];
    bottomContent: ExtractedContent[];
  },
  agent?: Tables<"agents"> | null,
) => {
  // If no agent provided, return original content
  if (!agent) return richContent;

  // Check if scheduling is enabled on the agent
  const schedulingEnabled =
    typeof agent?.skills_scheduling === "object" &&
    agent?.skills_scheduling !== null
      ? ((agent.skills_scheduling as Record<string, unknown>)
          .enabled as boolean)
      : false;

  // If scheduling is enabled, no filtering needed
  if (schedulingEnabled) return richContent;

  // Clone the rich content object to avoid mutating the original
  const filteredContent = {
    ...richContent,
    belowContent: [...richContent.belowContent],
    bottomContent: [...richContent.bottomContent],
  };

  // Filter out scheduling widgets if scheduling is not enabled
  filteredContent.belowContent = filteredContent.belowContent.filter(
    (content) => content.type !== "SCHEDULING" && content.type !== "CALENDLY",
  );

  // Filter out "I've booked a slot" suggestions
  filteredContent.bottomContent = filteredContent.bottomContent.filter(
    (content) =>
      !(
        content.type === "FOLLOWUP" &&
        content.content.toLowerCase().includes("booked")
      ),
  );

  return filteredContent;
};

/**
 * Validate content tag attributes and security
 */
export const validateContent = (
  type: RichContentTagType,
  attributes: Record<string, string>,
): boolean => {
  const config = RICH_CONTENT_TAGS[type];

  // Check required attributes
  if (config.requiredAttributes?.length) {
    const missingAttributes = config.requiredAttributes.filter(
      (attr) => !attributes[attr],
    );

    if (missingAttributes.length > 0) {
      console.warn(
        `Missing required attributes for ${type}: ${missingAttributes.join(", ")}`,
      );
      return false;
    }
  }

  // Validate domain for security (for iframe and embeds)
  if (type === "IFRAME" || type === "SCHEDULING") {
    const urlAttr = type === "IFRAME" ? "src" : "url";
    const url = attributes[urlAttr];

    if (url && config.allowedDomains?.length) {
      try {
        const domain = new URL(url).hostname;
        const isAllowed = config.allowedDomains.some(
          (allowedDomain) =>
            domain === allowedDomain || domain.endsWith(`.${allowedDomain}`),
        );

        if (!isAllowed) {
          console.warn(`Domain not allowed for ${type}: ${domain}`);
          return false;
        }
      } catch (e) {
        console.warn(`Invalid URL for ${type}: ${url}`);
        return false;
      }
    }
  }

  return true;
};

/**
 * Process content to extract follow-up suggestions (backward compatible)
 * @deprecated Use parseRichContent instead for new code
 */
export const parseFollowUpSuggestions = (content: string): string[] => {
  const suggestions: string[] = [];
  const regex = /<f>(.*?)<\/f>/g;
  let match;

  while ((match = regex.exec(content)) !== null) {
    suggestions.push(match[1]);
  }

  return suggestions;
};

/**
 * Process content to remove follow-up tags (backward compatible)
 * @deprecated Use parseRichContent instead for new code
 */
export const processMessageContent = (content: string): string => {
  return content.replace(/<f>.*?<\/f>/g, "");
};
